import React from 'react';
import { FD160 } from '../../../services/d206Api';
import { dateToInput, inputToDate, fmt, warehouseTypeLabel } from '../helpers';
import LookupField from '../../../components/LookupField';

interface Tab1Props {
  formData: Partial<FD160>;
  isEditing: boolean;
  isFieldDisabled: (key: string) => boolean;
  getField: (key: keyof FD160) => FD160[keyof FD160];
  setField: (key: keyof FD160, value: FD160[keyof FD160]) => void;
  getFieldError: (field: string) => string | undefined;
  // Computed summary
  totalQty: number;
  totalAmount: number;
  taxAmount: number;
  totalWithTax: number;
  // Lookup display names
  subcontractorName?: string;
  purchaseReceiptName?: string;
  warehouseName?: string;
}

const WAREHOUSE_TYPE_OPTIONS = {
  A: '渤揚倉庫',
  B: '工廠',
};

const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(({
  formData,
  isEditing,
  isFieldDisabled,
  getField,
  setField,
  getFieldError,
  totalQty,
  totalAmount,
  taxAmount,
  totalWithTax,
  subcontractorName,
  purchaseReceiptName,
  warehouseName,
}) => {
  const warehouseType = (getField('fd160_18') as string) || '';

  return (
    <div className="tab1-layout">
      <div className="form-sections">
        {/* Section 1: 基本欄位 */}
        <div className="form-section odd">
          <span className="section-tag">基本資料</span>
          <div className="form-grid">
            {/* 繳庫單號 fd160_11 */}
            <label className="required">繳庫單號</label>
            <div>
              <LookupField
                lookupType="purchaseReceipt"
                title="選擇繳庫單"
                value={(getField('fd160_11') as string) || ''}
                onChange={v => setField('fd160_11', v)}
                disabled={isFieldDisabled('fd160_11')}
                maxLength={8}
                style={{ width: 200 }} contractFieldId="fd160_11"
              />
              {purchaseReceiptName && <span className="lookup-name">{purchaseReceiptName}</span>}
              {getFieldError('fd160_11') && (
                <div className="field-error">{getFieldError('fd160_11')}</div>
              )}
            </div>

            {/* 退回日期 fd160_12 */}
            <label>退回日期</label>
            <div>
              <input
                type="date"
                value={dateToInput(getField('fd160_12') as string | null)}
                onChange={e => setField('fd160_12', inputToDate(e.target.value) as unknown as FD160[keyof FD160])}
                disabled={isFieldDisabled('fd160_12')}
                style={{ width: 160 }} data-contract-field="fd160_12"
              />
            </div>

            {/* 出庫類別 fd160_18 (combobox) */}
            <label className="required">出庫類別</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select
                value={warehouseType}
                onChange={e => setField('fd160_18', e.target.value)}
                disabled={isFieldDisabled('fd160_18')}
                style={{ width: 140 }} data-contract-field="fd160_18"
              >
                <option value="">-- 選擇 --</option>
                {Object.entries(WAREHOUSE_TYPE_OPTIONS).map(([code, label]) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
              <span className="lookup-name" style={{ border: 'none', background: 'transparent', minWidth: 0 }}>
                {warehouseType ? warehouseTypeLabel(warehouseType) : ''}
              </span>
              {getFieldError('fd160_18') && (
                <div className="field-error">{getFieldError('fd160_18')}</div>
              )}
            </div>

            {/* 出庫庫位 fd160_13 */}
            <label className="required">出庫庫位</label>
            <div>
              <LookupField
                lookupType="warehouse"
                title="選擇出庫庫位"
                value={(getField('fd160_13') as string) || ''}
                onChange={v => setField('fd160_13', v)}
                disabled={isFieldDisabled('fd160_13')}
                maxLength={5}
                style={{ width: 180 }} contractFieldId="fd160_13"
              />
              {warehouseName && <span className="lookup-name">{warehouseName}</span>}
              {getFieldError('fd160_13') && (
                <div className="field-error">{getFieldError('fd160_13')}</div>
              )}
            </div>

            {/* 分包商代號 fd160_10 */}
            <label>分包商代號</label>
            <div>
              <LookupField
                lookupType="vendor"
                title="選擇分包商"
                value={(getField('fd160_10') as string) || ''}
                onChange={v => setField('fd160_10', v)}
                disabled={isFieldDisabled('fd160_10')}
                maxLength={12}
                style={{ width: 200 }} contractFieldId="fd160_10"
              />
              {subcontractorName && <span className="lookup-name">{subcontractorName}</span>}
            </div>

            {/* 出庫通知單號 fd160_19 (功能已停用) */}
            <label data-contract-field="fd160_19">出庫通知單號</label>
            <div>
              <input
                type="text"
                value={(getField('fd160_19') as string) || ''}
                onChange={e => setField('fd160_19', e.target.value)}
                disabled={isFieldDisabled('fd160_19')}
                maxLength={5}
                style={{ width: 100 }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Card (right side) */}
      <div className="summary-card">
        <div className="summary-title">金額匯總</div>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="label">總退回數量</span>
            <span className="value">{fmt(totalQty)}</span>
          </div>
          <div className="summary-item">
            <span className="label">未稅退貨總額</span>
            <span className="value">{fmt(totalAmount)}</span>
          </div>
          <div className="summary-item">
            <span className="label">退貨總稅額</span>
            <span className="value">{fmt(taxAmount)}</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item total">
            <span className="label">含稅退貨總金額</span>
            <span className="value">{fmt(totalWithTax)}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

Tab1BasicInfo.displayName = 'Tab1BasicInfo';
export default Tab1BasicInfo;
